"use client";

import { useLayoutEffect, useRef, type JSX, type ReactNode } from "react";

interface ParallaxPageLayoutProps {
  header: ReactNode;
  children: ReactNode;
}

/**
 * Layout that creates a parallax overlap effect where the page content
 * (with rounded top corners) scrolls up and over the header banner.
 *
 * The sticky `top` is clamped to `min(navOffset, 100svh - headerHeight)`
 * so headers taller than the viewport scroll fully into view before
 * pinning by their bottom edge — otherwise the bottom of an oversized
 * header would stay permanently hidden behind the overlapping content.
 *
 * `svh` (not `dvh`) keeps the clamp stable while mobile browser chrome
 * collapses during scroll, and the measured height is written straight
 * to the DOM (not React state) so scroll/resize never re-renders.
 *
 * The header's sticky scope is bounded by a spacer so it unpins and scrolls
 * away once the content has covered it (cover distance = headerHeight − overlap),
 * instead of staying pinned behind the page for the full scroll — which on slow
 * frames let the banner bleed through unpainted content (VAW-1213). The spacer
 * and the content's negative top margin both derive from --parallax-header-height,
 * so the visible overlap (32px / 40px) is unchanged.
 */
export function ParallaxPageLayout({
  header,
  children,
}: ParallaxPageLayoutProps): JSX.Element {
  const headerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = headerRef.current;
    const container = containerRef.current;
    if (!el || !container) return;
    let lastHeight = -1;
    const update = (): void => {
      const height = Math.round(el.offsetHeight);
      if (height === lastHeight) return;
      lastHeight = height;
      container.style.setProperty("--parallax-header-height", `${height}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div>
        <div
          ref={headerRef}
          data-page-header-banner
          className="sticky z-0 top-[min(var(--nav-offset-mobile,112px),calc(100svh_-_var(--parallax-header-height,0px)))] desktop:top-[min(var(--nav-offset-desktop,145px),calc(100svh_-_var(--parallax-header-height,0px)))]"
        >
          {header}
        </div>
        <div
          aria-hidden
          className="h-[max(0px,calc(var(--parallax-header-height,0px)_-_2rem))] desktop-sm:h-[max(0px,calc(var(--parallax-header-height,0px)_-_2.5rem))]"
        />
      </div>

      <div
        data-parallax-content
        className="relative z-10 mt-[calc(-1*max(var(--parallax-header-height,0px),2rem))] rounded-t-xl bg-surface-base-primary desktop-sm:mt-[calc(-1*max(var(--parallax-header-height,0px),2.5rem))]"
      >
        {children}
      </div>
    </div>
  );
}
